import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { CircleNotch, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [message, setMessage] = useState("Confirming your email…");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React 18 StrictMode double-run so we don't burn the token twice
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("This link is missing its verification code.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/magic-link/verify", { token });
        setStatus("ok");
        setMessage("Email confirmed! Taking you to your dashboard…");
        await checkAuth();
        setTimeout(() => navigate("/dashboard", { replace: true }), 900);
      } catch (e) {
        setStatus("error");
        setMessage(e?.response?.data?.detail || "We couldn't confirm this link.");
      }
    })();
  }, [params, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-6" data-testid="verify-page">
      <div className="w-full max-w-md text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
        <img src="/controlly-icon.png" alt="Controlly" className="w-12 h-12 rounded-md mx-auto mb-6" />
        {status === "verifying" && <CircleNotch size={40} className="text-blue-500 animate-spin mx-auto" />}
        {status === "ok" && <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto" />}
        {status === "error" && <WarningCircle size={44} weight="fill" className="text-red-500 mx-auto" />}
        <p className="mt-5 text-zinc-200 text-base" data-testid="verify-message">{message}</p>
        {status === "error" && (
          <Link to="/login" data-testid="verify-back-to-login" className="inline-block mt-6 rounded-full bg-white text-black text-sm font-semibold px-5 py-2.5 hover:bg-zinc-200 transition-colors">
            Back to sign up
          </Link>
        )}
      </div>
    </div>
  );
}
